import { AppError, convertDistance, } from "@hisani/core";
export const STOPWATCH_ERROR_CODES = {
    emptySession: "STOPWATCH_EMPTY_SESSION",
    invalidCalculation: "STOPWATCH_INVALID_CALCULATION",
    invalidClock: "STOPWATCH_INVALID_CLOCK",
    invalidTransition: "STOPWATCH_INVALID_TRANSITION",
};
export const STOPWATCH_SECTIONAL_DISTANCES_METERS = [
    200, 400, 600, 800, 1_000, 1_200, 1_400, 1_600,
];
export function isStopwatchSectionalDistanceMeters(distanceMeters) {
    return STOPWATCH_SECTIONAL_DISTANCES_METERS.some((supportedDistance) => supportedDistance === distanceMeters);
}
export function calculateCumulativeDistanceMeters(sectionals) {
    return sectionals.reduce((total, sectional) => total + sectional.distanceMeters, 0);
}
export function createStopwatchRun(id, label = null) {
    return {
        id,
        label,
        sectionals: [],
        status: "idle",
    };
}
export function transitionStopwatchRun(run, command, clock) {
    switch (command.type) {
        case "RENAME":
            return { ...run, label: command.label };
        case "START":
            requireStatus(run, "idle", command.type);
            return {
                ...run,
                status: "running",
                accumulatedMilliseconds: 0,
                startedAtMilliseconds: readClock(clock),
            };
        case "PAUSE": {
            requireStatus(run, "running", command.type);
            const accumulatedMilliseconds = elapsedWhileRunning(run, clock);
            return {
                id: run.id,
                label: run.label,
                sectionals: run.sectionals,
                status: "paused",
                accumulatedMilliseconds,
            };
        }
        case "RESUME":
            requireStatus(run, "paused", command.type);
            return {
                id: run.id,
                label: run.label,
                sectionals: run.sectionals,
                status: "running",
                accumulatedMilliseconds: run.accumulatedMilliseconds,
                startedAtMilliseconds: readClock(clock),
            };
        case "RECORD_SECTIONAL": {
            requireStatus(run, "running", command.type);
            requireSectionalDistance(command.distanceMeters);
            const cumulativeMilliseconds = elapsedWhileRunning(run, clock);
            const previousSectional = run.sectionals.at(-1);
            const splitMilliseconds = cumulativeMilliseconds -
                (previousSectional?.cumulativeMilliseconds ?? 0);
            return {
                ...run,
                sectionals: [
                    ...run.sectionals,
                    {
                        sequence: run.sectionals.length + 1,
                        distanceMeters: command.distanceMeters,
                        cumulativeMilliseconds,
                        splitMilliseconds,
                    },
                ],
            };
        }
        case "STOP":
            if (run.status === "running") {
                return {
                    id: run.id,
                    label: run.label,
                    sectionals: run.sectionals,
                    status: "stopped",
                    elapsedMilliseconds: elapsedWhileRunning(run, clock),
                };
            }
            if (run.status === "paused") {
                return {
                    id: run.id,
                    label: run.label,
                    sectionals: run.sectionals,
                    status: "stopped",
                    elapsedMilliseconds: run.accumulatedMilliseconds,
                };
            }
            throw invalidTransition(run.status, command.type);
        case "RESET":
            return createStopwatchRun(run.id, run.label);
        default:
            return assertNever(command);
    }
}
function requireSectionalDistance(distanceMeters) {
    if (!isStopwatchSectionalDistanceMeters(distanceMeters)) {
        throw new AppError({
            code: STOPWATCH_ERROR_CODES.invalidCalculation,
            message: "The sectional distance is not supported.",
        });
    }
}
export function finishStopwatchSessionRuns(runs, clock) {
    const timedRuns = runs.filter((run) => run.status !== "idle");
    if (timedRuns.length === 0) {
        throw new AppError({
            code: STOPWATCH_ERROR_CODES.emptySession,
            message: "A Stopwatch session requires at least one timed run.",
        });
    }
    const sharedStopReading = timedRuns.some((run) => run.status === "running")
        ? readClock(clock)
        : 0;
    const sharedStopClock = {
        nowMilliseconds: () => sharedStopReading,
    };
    return timedRuns.map((run) => {
        if (run.status === "stopped") {
            return run;
        }
        const stoppedRun = transitionStopwatchRun(run, { type: "STOP" }, sharedStopClock);
        if (stoppedRun.status !== "stopped") {
            throw invalidTransition(stoppedRun.status, "STOP");
        }
        return stoppedRun;
    });
}
export function getElapsedMilliseconds(run, clock) {
    switch (run.status) {
        case "idle":
            return 0;
        case "running":
            return elapsedWhileRunning(run, clock);
        case "paused":
            return run.accumulatedMilliseconds;
        case "stopped":
            return run.elapsedMilliseconds;
        default:
            return assertNever(run);
    }
}
export function calculateAverageSpeedMetersPerSecond(distance, durationMilliseconds) {
    if (!Number.isFinite(distance.value) ||
        distance.value < 0 ||
        !Number.isSafeInteger(durationMilliseconds) ||
        durationMilliseconds <= 0) {
        throw new AppError({
            code: STOPWATCH_ERROR_CODES.invalidCalculation,
            message: "Average speed requires a valid distance and positive duration.",
        });
    }
    const distanceInMeters = convertDistance(distance, "METERS").value;
    return distanceInMeters / (durationMilliseconds / 1_000);
}
export function formatElapsedToHundredths(durationMilliseconds) {
    if (!Number.isSafeInteger(durationMilliseconds) || durationMilliseconds < 0) {
        throw new AppError({
            code: STOPWATCH_ERROR_CODES.invalidCalculation,
            message: "Elapsed display requires a non-negative integer duration.",
        });
    }
    const totalHundredths = Math.floor(durationMilliseconds / 10);
    const hundredths = totalHundredths % 100;
    const totalSeconds = Math.floor(totalHundredths / 100);
    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60);
    return `${pad(minutes)}:${pad(seconds)}.${pad(hundredths)}`;
}
function elapsedWhileRunning(run, clock) {
    const nowMilliseconds = readClock(clock);
    if (nowMilliseconds < run.startedAtMilliseconds) {
        throw new AppError({
            code: STOPWATCH_ERROR_CODES.invalidClock,
            message: "The monotonic clock moved backwards.",
        });
    }
    return (run.accumulatedMilliseconds +
        Math.floor(nowMilliseconds - run.startedAtMilliseconds));
}
function readClock(clock) {
    const reading = clock.nowMilliseconds();
    if (!Number.isFinite(reading) || reading < 0) {
        throw new AppError({
            code: STOPWATCH_ERROR_CODES.invalidClock,
            message: "The monotonic clock returned an invalid reading.",
        });
    }
    return reading;
}
function requireStatus(run, expected, command) {
    if (run.status !== expected) {
        throw invalidTransition(run.status, command);
    }
}
function invalidTransition(status, command) {
    return new AppError({
        code: STOPWATCH_ERROR_CODES.invalidTransition,
        message: `Cannot apply ${command} while a Stopwatch run is ${status}.`,
    });
}
function pad(value) {
    return String(value).padStart(2, "0");
}
function assertNever(value) {
    throw new AppError({
        code: STOPWATCH_ERROR_CODES.invalidTransition,
        message: `Unsupported Stopwatch state or command: ${String(value)}`,
    });
}
